import { useState } from 'react'
import type { TreatmentDetail } from '../../store/treatmentsSlice'
import type { Medication } from '../../store/medicationsSlice'
import type { Veterinarian } from '../../store/settingsSlice'

interface NewPrescriptionModalProps {
  treatment: TreatmentDetail
  medications: Medication[]
  veterinarians: Veterinarian[]
  onSubmit: (data: {
    patient_id: string
    medication_id: string
    treatment_id: string
    veterinarian_id: string
    dosage: string
    frequency: string
    route: string
    duration: string
    quantity_dispensed: number
    total_refills: number
    start_date: string
  }) => Promise<void>
  onClose: () => void
}

export function NewPrescriptionModal({ treatment, medications, veterinarians, onSubmit, onClose }: NewPrescriptionModalProps) {
  const [medicationId, setMedicationId] = useState('')
  const [medSearch, setMedSearch] = useState('')
  const [vetId, setVetId] = useState(treatment.veterinarian_id || '')
  const [vetDropdownOpen, setVetDropdownOpen] = useState(false)
  const [dosage, setDosage] = useState('')
  const [frequency, setFrequency] = useState('BID')
  const [frequencyDropdownOpen, setFrequencyDropdownOpen] = useState(false)
  const [route, setRoute] = useState('Oral')
  const [duration, setDuration] = useState('')
  const [quantity, setQuantity] = useState('')
  const [totalRefills, setTotalRefills] = useState('0')
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10))

  const filteredMeds = medications.filter((m) =>
    m.name.toLowerCase().includes(medSearch.toLowerCase())
  )

  const handleSubmit = async () => {
    if (!medicationId) return
    await onSubmit({
      patient_id: treatment.patient_id,
      medication_id: medicationId,
      treatment_id: treatment.id,
      veterinarian_id: vetId,
      dosage,
      frequency,
      route,
      duration,
      quantity_dispensed: parseInt(quantity) || 0,
      total_refills: parseInt(totalRefills) || 0,
      start_date: startDate,
    })
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" data-testid="new-prescription-modal">
      <div className="bg-surface rounded-[8px] shadow-elevation-2 w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto">
        <h2 className="text-[14px] font-semibold text-text-primary mb-4">New Prescription</h2>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-[12px] text-text-muted">Patient</span>
            <span className="text-[13px] text-text-primary font-medium" data-testid="new-rx-patient-name">
              {treatment.patient_name}
            </span>
          </div>

          <label className="flex flex-col gap-1 text-[12px] text-text-muted">
            Medication
            <input
              data-testid="new-rx-medication-search"
              value={medSearch}
              onChange={(e) => {
                setMedSearch(e.target.value)
                setMedicationId('')
              }}
              placeholder="Search medications..."
              className="h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
            />
            {medSearch && !medicationId && filteredMeds.length > 0 && (
              <div className="border border-border rounded-[4px] bg-surface max-h-32 overflow-y-auto">
                {filteredMeds.map((m) => (
                  <button
                    key={m.id}
                    data-testid="medication-option"
                    onClick={() => {
                      setMedicationId(m.id)
                      setMedSearch(m.name)
                      if (m.default_dose_rate) {
                        setDosage(`${m.default_dose_rate} mg/kg`)
                      }
                      if (m.common_frequency) {
                        setFrequency(m.common_frequency)
                      }
                    }}
                    className="block w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                  >
                    {m.name} ({m.concentration})
                  </button>
                ))}
              </div>
            )}
          </label>

          <div className="flex flex-col gap-1 text-[12px] text-text-muted">
            <span>Prescribing Veterinarian</span>
            <div className="relative">
              <button
                type="button"
                data-testid="new-rx-vet-select"
                onClick={() => setVetDropdownOpen(!vetDropdownOpen)}
                className="w-full h-7 px-2 text-left text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent cursor-pointer"
              >
                {veterinarians.find((v) => v.id === vetId)?.name || 'Select vet...'}
              </button>
              {vetDropdownOpen && (
                <div
                  data-testid="new-rx-vet-dropdown"
                  className="absolute z-10 top-full left-0 mt-1 w-full border border-border rounded-[4px] bg-surface max-h-48 overflow-y-auto"
                  style={{ boxShadow: 'var(--shadow-elevation-1)' }}
                >
                  {veterinarians.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      data-testid={`vet-option-${v.id}`}
                      onClick={() => {
                        setVetId(v.id)
                        setVetDropdownOpen(false)
                      }}
                      className="block w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                    >
                      {v.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <label className="flex flex-col gap-1 text-[12px] text-text-muted">
            Dosage
            <input
              data-testid="new-rx-dosage"
              value={dosage}
              onChange={(e) => setDosage(e.target.value)}
              placeholder="e.g., 75mg"
              className="h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
            />
          </label>

          <div className="flex flex-col gap-1 text-[12px] text-text-muted">
            <span>Frequency</span>
            <div className="relative">
              <button
                type="button"
                data-testid="new-rx-frequency"
                onClick={() => setFrequencyDropdownOpen(!frequencyDropdownOpen)}
                className="w-full h-7 px-2 text-left text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent cursor-pointer"
              >
                {frequency || 'Select frequency...'}
              </button>
              {frequencyDropdownOpen && (
                <div
                  data-testid="new-rx-frequency-dropdown"
                  className="absolute z-10 top-full left-0 mt-1 w-full border border-border rounded-[4px] bg-surface max-h-48 overflow-y-auto"
                  style={{ boxShadow: 'var(--shadow-elevation-1)' }}
                >
                  {['SID', 'BID', 'TID', 'QID', 'q8h', 'q12h', 'PRN'].map((f) => (
                    <button
                      key={f}
                      type="button"
                      data-testid={`frequency-option-${f}`}
                      onClick={() => {
                        setFrequency(f)
                        setFrequencyDropdownOpen(false)
                      }}
                      className="block w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                    >
                      {f}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <label className="flex flex-col gap-1 text-[12px] text-text-muted">
            Route
            <input
              data-testid="new-rx-route"
              value={route}
              onChange={(e) => setRoute(e.target.value)}
              placeholder="e.g., Oral"
              className="h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
            />
          </label>

          <label className="flex flex-col gap-1 text-[12px] text-text-muted">
            Duration
            <input
              data-testid="new-rx-duration"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="e.g., 14 days"
              className="h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
            />
          </label>

          <div className="flex gap-3">
            <label className="flex flex-col gap-1 text-[12px] text-text-muted flex-1">
              Quantity
              <input
                data-testid="new-rx-quantity"
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
              />
            </label>
            <label className="flex flex-col gap-1 text-[12px] text-text-muted flex-1">
              Total Refills
              <input
                data-testid="new-rx-refills"
                type="number"
                value={totalRefills}
                onChange={(e) => setTotalRefills(e.target.value)}
                className="h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
              />
            </label>
          </div>

          <label className="flex flex-col gap-1 text-[12px] text-text-muted">
            Start Date
            <input
              data-testid="new-rx-start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="h-7 px-2 text-[13px] text-text-primary border border-border rounded-[4px] bg-base outline-none focus:border-accent"
            />
          </label>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button
            data-testid="new-rx-cancel-button"
            onClick={onClose}
            className="inline-flex items-center h-7 px-3 rounded-[4px] border border-border text-[13px] text-text-secondary hover:bg-hover transition-colors duration-100 cursor-pointer"
          >
            Cancel
          </button>
          <button
            data-testid="new-rx-submit-button"
            onClick={handleSubmit}
            disabled={!medicationId}
            className="inline-flex items-center h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] font-normal hover:bg-accent-dark transition-colors duration-100 cursor-pointer disabled:opacity-50 disabled:cursor-default"
          >
            Create Prescription
          </button>
        </div>
      </div>
    </div>
  )
}
